import { TriangleAlert, ChevronLeft, FileText, LogOut, Menu } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Toaster } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { navigationPath } from '../../App';
import Home from './home';
import Discount from './discount';
import SlotDemo from './slot-demo';
import ApkLite from './apk-lite';
import ConfirmDialog from './components/logout-dialog';
import SideDrawer, { LogTable, Pagination } from './components/side-drawer';
import {
  fetchErrorLogs,
  fetchChangeLogs,
  deleteErrorLogs,
  deleteChangeLogs,
  checkTokenValidity,
  logOut,
  fetchHomeData,
  type ErrorLog,
  type ChangeLog,
  type PaginatedResponse,
} from '../../services/dashboard-service';

const Dashboard = () => {
  const navigate = useNavigate();

  const [namaWeb, setNamaWeb] = useState('');
  const [selectedMenu, setSelectedMenu] = useState('1');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const [showLogDrawer, setShowLogDrawer] = useState<'error' | 'changelog' | null>(null);
  const [errorLogsResponse, setErrorLogsResponse] = useState<PaginatedResponse<ErrorLog>>({
    message: '',
    data: [],
    currentPage: 1,
    totalPages: 0,
    totalItems: 0,
  });
  const [changeLogsResponse, setChangeLogsResponse] = useState<PaginatedResponse<ChangeLog>>({
    message: '',
    data: [],
    currentPage: 1,
    totalPages: 0,
    totalItems: 0,
  });

  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const currentData = showLogDrawer === 'error' ? errorLogsResponse.data : changeLogsResponse.data;
  const totalItems = showLogDrawer === 'error' ? errorLogsResponse.totalItems : changeLogsResponse.totalItems;
  const currentPage = showLogDrawer === 'error' ? errorLogsResponse.currentPage : changeLogsResponse.currentPage;
  const totalPages = showLogDrawer === 'error' ? errorLogsResponse.totalPages : changeLogsResponse.totalPages;

  useEffect(() => {
    const validateToken = async () => {
      const isValid = await checkTokenValidity();
      if (isValid) navigate(navigationPath.dashboard);
      else navigate(navigationPath.login);
    };
    validateToken();
  }, [navigate]);

  useEffect(() => {
    const loadHomeData = async () => {
      const data = await fetchHomeData();
      setNamaWeb(data.namaWeb);
    };
    loadHomeData();
  }, []);

  const handleLogOut = () => {
    setShowLogoutConfirm(true);
  };

  const confirmLogOut = async () => {
    const success = await logOut();
    if (success) {
      navigate(navigationPath.logout);
      localStorage.removeItem('accessToken');
    }
    setShowLogoutConfirm(false);
  };

  const handleShowLogs = async (type: 'error' | 'changelog') => {
    if (type === 'error') {
      const response = await fetchErrorLogs();
      setErrorLogsResponse(response);
    } else {
      const response = await fetchChangeLogs();
      setChangeLogsResponse(response);
    }
    setShowLogDrawer(type);
  };

  const handleCloseDrawer = () => {
    setShowLogDrawer(null);
  };

  const handleDeleteLogs = () => {
    setShowDeleteConfirm(true);
  };

  const confirmDeleteLogs = async () => {
    let success = false;

    if (showLogDrawer === 'error') {
      success = await deleteErrorLogs();
      if (success) {
        setErrorLogsResponse({
          message: '',
          data: [],
          currentPage: 1,
          totalPages: 0,
          totalItems: 0,
        });
      }
    } else if (showLogDrawer === 'changelog') {
      success = await deleteChangeLogs();
      if (success) {
        setChangeLogsResponse({
          message: '',
          data: [],
          currentPage: 1,
          totalPages: 0,
          totalItems: 0,
        });
      }
    }

    if (success) {
      setShowLogDrawer(null);
    }

    setShowDeleteConfirm(false);
  };

  const handlePageChange = async (page: number) => {
    if (showLogDrawer === 'error') {
      const response = await fetchErrorLogs(page);
      setErrorLogsResponse(response);
    } else if (showLogDrawer === 'changelog') {
      const response = await fetchChangeLogs(page);
      setChangeLogsResponse(response);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      handlePageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      handlePageChange(currentPage + 1);
    }
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const menuItems = [
    {
      id: '1',
      title: 'Home',
      icon: (
        <svg
          xmlns='http://www.w3.org/2000/svg'
          className={`h-5 w-5 ${!sidebarCollapsed && 'mr-3'}`}
          fill='none'
          viewBox='0 0 24 24'
          stroke='currentColor'
        >
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth={2}
            d='M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6'
          />
        </svg>
      ),
    },
    {
      id: '2',
      title: 'Slot Demo',
      icon: (
        <svg
          xmlns='http://www.w3.org/2000/svg'
          className={`h-5 w-5 ${!sidebarCollapsed && 'mr-3'}`}
          fill='none'
          viewBox='0 0 24 24'
          stroke='currentColor'
        >
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth={2}
            d='M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4'
          />
        </svg>
      ),
    },
    {
      id: '3',
      title: 'Discount',
      icon: (
        <svg
          xmlns='http://www.w3.org/2000/svg'
          className={`h-5 w-5 ${!sidebarCollapsed && 'mr-3'}`}
          fill='none'
          viewBox='0 0 24 24'
          stroke='currentColor'
        >
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth={2}
            d='M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
          />
        </svg>
      ),
    },
    {
      id: '4',
      title: 'APK Lite',
      icon: (
        <svg
          xmlns='http://www.w3.org/2000/svg'
          className={`h-5 w-5 ${!sidebarCollapsed && 'mr-3'}`}
          fill='none'
          viewBox='0 0 24 24'
          stroke='currentColor'
        >
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth={2}
            d='M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z'
          />
        </svg>
      ),
    },
  ];

  const logDrawerConfig = {
    error: {
      title: 'Error Logs',
      icon: TriangleAlert,
      iconColor: 'text-red-500',
      deleteConfirmTitle: 'Konfirmasi Hapus',
      deleteConfirmMessage: 'Yakin ingin menghapus semua error log?',
      emptyMessage: {
        title: 'Tidak ada error log',
        description: 'Ketika terjadi error, akan muncul di sini',
      },
    },
    changelog: {
      title: 'Change Logs',
      icon: FileText,
      iconColor: 'text-yellow-500',
      deleteConfirmTitle: 'Konfirmasi Hapus',
      deleteConfirmMessage: 'Yakin ingin menghapus semua changelog?',
      emptyMessage: {
        title: 'Tidak ada log perubahan',
        description: 'Perubahan pada sistem akan tercatat di sini',
      },
    },
  };

  return (
    <div className='flex min-h-screen bg-[#141c2f]'>
      <Toaster position='top-right' toastOptions={{ duration: 3000 }} />

      <ConfirmDialog
        isOpen={showLogoutConfirm}
        title='Konfirmasi Logout'
        message='Yakin ingin keluar ?'
        onConfirm={confirmLogOut}
        onCancel={() => setShowLogoutConfirm(false)}
        confirmButtonText='Logout'
      />

      <div
        className={`${
          sidebarCollapsed ? 'w-16' : 'w-[230px]'
        } transition-all duration-300 ease-in-out fixed h-screen bg-[#1e2638] z-10`}
      >
        <div className='p-4 border-b border-[#2a334a] h-[64px] flex items-center'>
          {!sidebarCollapsed && <h1 className='text-xl font-bold text-white'>CMS</h1>}
          <button
            onClick={toggleSidebar}
            className={`${sidebarCollapsed ? 'mx-auto' : 'ml-auto'} text-gray-400 hover:text-white`}
          >
            {sidebarCollapsed ? <Menu size={20} /> : <ChevronLeft size={20} />}
          </button>
        </div>

        <nav className='mt-2'>
          <ul>
            {menuItems.map(item => (
              <li key={item.id}>
                <button
                  onClick={() => setSelectedMenu(item.id)}
                  className={`flex items-center w-full ${sidebarCollapsed ? 'justify-center px-0' : 'px-4'} py-3 ${
                    selectedMenu === item.id ? 'bg-[#4269ed] text-white' : 'text-gray-300 hover:bg-[#2a334a]'
                  }`}
                >
                  {item.icon}
                  {!sidebarCollapsed && <span>{item.title}</span>}
                </button>
              </li>
            ))}
          </ul>
        </nav>
      </div>

      <div className={`flex-1 ${sidebarCollapsed ? 'ml-16' : 'ml-[230px]'} transition-all duration-300 ease-in-out`}>
        <header
          className='bg-[#1e2638] h-[64px] flex items-center justify-between px-4 border-b border-[#2a334a] fixed top-0 right-0 left-0 z-10'
          style={{ left: sidebarCollapsed ? '64px' : '230px' }}
        >
          <h1 className='text-xl font-bold text-white'>{`APP ANTI NAWALA CMS - ${namaWeb || 'GAS'}`}</h1>
          <div className='flex items-center gap-4'>
            <button
              onClick={() => handleShowLogs('error')}
              className='p-2 text-white rounded-full hover:bg-[#2a334a] transition-colors'
              title='Error Log'
            >
              <TriangleAlert size={20} />
            </button>
            <button
              onClick={() => handleShowLogs('changelog')}
              className='p-2 text-white rounded-full hover:bg-[#2a334a] transition-colors'
              title='Change Log'
            >
              <FileText size={20} />
            </button>
            <div className='bg-red-600/80 rounded-lg p-1 hover:bg-red-700/80 transition-colors'>
              <button
                onClick={handleLogOut}
                className='p-2 text-white rounded-full hover:bg-[#2a334a] transition-colors'
                title='Log Out'
              >
                <LogOut size={20} />
              </button>
            </div>
          </div>
        </header>

        <div className='p-6 text-white mt-[64px]'>
          {selectedMenu === '1' && <Home />}
          {selectedMenu === '2' && <SlotDemo />}
          {selectedMenu === '3' && <Discount />}
          {selectedMenu === '4' && <ApkLite />}
        </div>
      </div>

      {showLogDrawer && (
        <SideDrawer
          isOpen={!!showLogDrawer}
          title={logDrawerConfig[showLogDrawer].title}
          icon={logDrawerConfig[showLogDrawer].icon}
          iconColor={logDrawerConfig[showLogDrawer].iconColor}
          onClose={handleCloseDrawer}
          onDelete={handleDeleteLogs}
          showDeleteConfirm={showDeleteConfirm}
          onCancelDelete={() => setShowDeleteConfirm(false)}
          onConfirmDelete={confirmDeleteLogs}
          deleteConfirmTitle={logDrawerConfig[showLogDrawer].deleteConfirmTitle}
          deleteConfirmMessage={logDrawerConfig[showLogDrawer].deleteConfirmMessage}
        >
          <div className='flex-1 bg-[#212a40] rounded-md overflow-hidden border border-[#2d3649]'>
            <LogTable type={showLogDrawer} data={currentData} emptyMessage={logDrawerConfig[showLogDrawer].emptyMessage} />
          </div>

          {totalItems > 0 && (
            <Pagination
              currentPage={currentPage}
              totalItems={totalItems}
              itemsPerPage={20}
              onPrevious={handlePrevPage}
              onNext={handleNextPage}
            />
          )}
        </SideDrawer>
      )}
    </div>
  );
};

export default Dashboard;
